import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { getPaddleEnvironment } from '@/lib/paddle';

export type SubscriptionPlan = 'free' | 'free_pro' | 'pro' | 'university' | 'student' | 'enterprise' | 'team' | 'lapsed_pro';

interface PlanLimits {
  max_papers: number;
  max_references: number;
  max_ai_quick_fill_per_month: number;
  max_collections: number;
  max_reference_sources_per_collection: number;
  max_reference_styles_per_collection: number;
  max_paste_imports_per_batch: number;
  max_attachments_per_reference: number;
  max_attachment_size_mb: number;
  max_storage_mb: number;
  can_publish_to_discover: boolean;
  can_save_private_templates: boolean;
  can_copy_discover_templates: boolean;
  can_share_papers_publicly: boolean;
  can_export_references: boolean;
  favorites_enabled: boolean;
  discover_access: string;
  upgrade_message: string | null;
}

interface UserUsage {
  papers_count: number;
  references_count: number;
  ai_quick_fill_count: number;
  ai_quick_fill_reset_at: string;
  collections_count: number;
  styles_count: number;
  sources_count: number;
}

export type LimitType = 'papers' | 'references' | 'ai_quick_fill' | 'collections' | 'styles' | 'sources';

interface SubscriptionContextType {
  plan: SubscriptionPlan;
  limits: PlanLimits | null;
  usage: UserUsage | null;
  loading: boolean;
  isAtLimit: (limitType: LimitType, currentCount?: number) => boolean;
  hasAnyLimitReached: boolean;
  incrementUsage: (usageType: 'ai_quick_fill') => Promise<void>;
  refreshUsage: () => Promise<void>;
  checkStripeSubscription: () => Promise<void>;
  isPro: boolean;
  isUniversity: boolean;
  subscriptionEnd: string | null;
  cancelAtPeriodEnd: boolean;
  wasPro: boolean;
  isLapsedPro: boolean;
  hasPaidSubscription: boolean;
  billingPriceId: string | null;
  scheduledPriceId: string | null;
  paymentFailed: boolean;
  isTestSubscription: boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user, session, loading: authLoading } = useAuth();
  const [plan, setPlan] = useState<SubscriptionPlan>('free');
  const [limits, setLimits] = useState<PlanLimits | null>(null);
  const [usage, setUsage] = useState<UserUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [wasPro, setWasPro] = useState(false);
  const [hasPaidSubscription, setHasPaidSubscription] = useState(false);
  const [billingPriceId, setBillingPriceId] = useState<string | null>(null);
  const [scheduledPriceId, setScheduledPriceId] = useState<string | null>(null);
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [isTestSubscription, setIsTestSubscription] = useState(false);

  const fetchLocalSubscriptionData = async () => {
    if (!user) {
      setPlan('free');
      setLimits(null);
      setUsage(null);
      setLoading(false);
      return;
    }

    try {
      // Fetch subscription
      const { data: realSub } = await supabase
        .from('user_subscriptions')
        .select('plan, current_period_end, cancel_at_period_end, was_pro')
        .eq('user_id', user.id)
        .maybeSingle();
      let subscription: any = realSub;
      // In the preview (test mode), a test purchase is shown on top of the real plan.
      if (getPaddleEnvironment() === 'sandbox') {
        const { data: testSub } = await supabase
          .from('test_subscriptions')
          .select('plan, current_period_end, cancel_at_period_end, was_pro, paddle_subscription_id')
          .eq('user_id', user.id)
          .maybeSingle();
        if (testSub && (testSub.paddle_subscription_id || testSub.plan !== 'free')) {
          subscription = { ...testSub, was_pro: realSub?.was_pro || testSub.was_pro };
        }
      }

      const rawPlan = (subscription?.plan as SubscriptionPlan) || 'free';
      const isLapsed = rawPlan === 'free' && (subscription?.was_pro || false);
      const currentPlan = isLapsed ? 'lapsed_pro' as SubscriptionPlan : rawPlan;
      setPlan(currentPlan);
      setSubscriptionEnd(subscription?.current_period_end || null);
      setCancelAtPeriodEnd(subscription?.cancel_at_period_end || false);
      setWasPro(subscription?.was_pro || false);

      // Fetch limits for current plan (use 'lapsed_pro' limits if user is lapsed pro)
      const limitsKey = isLapsed ? 'lapsed_pro' : currentPlan;
      const { data: planLimits } = await (supabase
        .from('plan_limits_public' as any)
        .select('*')
        .eq('plan', limitsKey)
        .maybeSingle()) as { data: any };

      if (planLimits) {
        setLimits({
          max_papers: planLimits.max_papers,
          max_references: planLimits.max_references,
          max_ai_quick_fill_per_month: planLimits.max_ai_quick_fill_per_month,
          max_collections: planLimits.max_collections ?? -1,
          max_reference_sources_per_collection: planLimits.max_reference_sources_per_collection ?? -1,
          max_reference_styles_per_collection: planLimits.max_reference_styles_per_collection ?? -1,
          max_paste_imports_per_batch: planLimits.max_paste_imports_per_batch ?? 3,
          max_attachments_per_reference: planLimits.max_attachments_per_reference ?? 3,
          max_attachment_size_mb: planLimits.max_attachment_size_mb ?? 10,
          max_storage_mb: planLimits.max_storage_mb ?? 300,
          can_save_private_templates: planLimits.can_save_private_templates ?? false,
          can_publish_to_discover: planLimits.can_publish_to_discover ?? false,
          can_copy_discover_templates: planLimits.can_copy_discover_templates ?? false,
          can_share_papers_publicly: planLimits.can_share_papers_publicly ?? false,
          can_export_references: planLimits.can_export_references ?? false,
          favorites_enabled: planLimits.favorites_enabled ?? false,
          discover_access: planLimits.discover_access ?? 'disabled',
          upgrade_message: planLimits.upgrade_message ?? null,
        });
      }

      // Fetch usage
      const { data: userUsage } = await supabase
        .from('user_usage')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (userUsage) {
        setUsage({
          papers_count: userUsage.papers_count,
          references_count: userUsage.references_count,
          ai_quick_fill_count: userUsage.ai_quick_fill_count,
          ai_quick_fill_reset_at: userUsage.ai_quick_fill_reset_at,
          collections_count: userUsage.collections_count,
          styles_count: userUsage.styles_count,
          sources_count: userUsage.sources_count,
        });
      }
    } catch (error) {
      console.error('Error fetching subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkStripeSubscription = useCallback(async () => {
    // Don't call if auth is still loading or no valid session
    if (authLoading || !session?.access_token) return;

    try {
      const { data, error } = await supabase.functions.invoke('check-subscription', {
        body: { environment: getPaddleEnvironment() },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('Error checking Stripe subscription:', error);
        return;
      }

      if (data) {
        const newPlan = (data.plan as SubscriptionPlan) || 'free';
        setPlan(newPlan);
        setSubscriptionEnd(data.subscription_end || null);
        setCancelAtPeriodEnd(data.cancel_at_period_end || false);
        setWasPro(data.was_pro || false);
        setHasPaidSubscription(!!data.has_paid_subscription);
        setBillingPriceId(data.price_id || null);
        setScheduledPriceId(data.scheduled_price_id || null);
        setPaymentFailed(!!data.payment_failed);
        setIsTestSubscription(!!data.test_mode);

        // Fetch updated limits for the new plan
        const { data: planLimits } = await (supabase
          .from('plan_limits_public' as any)
          .select('*')
          .eq('plan', newPlan)
          .maybeSingle()) as { data: any };

        if (planLimits) {
          setLimits({
            max_papers: planLimits.max_papers,
            max_references: planLimits.max_references,
            max_ai_quick_fill_per_month: planLimits.max_ai_quick_fill_per_month,
            max_collections: planLimits.max_collections ?? -1,
            max_reference_sources_per_collection: planLimits.max_reference_sources_per_collection ?? -1,
            max_reference_styles_per_collection: planLimits.max_reference_styles_per_collection ?? -1,
            max_paste_imports_per_batch: planLimits.max_paste_imports_per_batch ?? 3,
            max_attachments_per_reference: planLimits.max_attachments_per_reference ?? 3,
            max_attachment_size_mb: planLimits.max_attachment_size_mb ?? 10,
            max_storage_mb: planLimits.max_storage_mb ?? 300,
            can_save_private_templates: planLimits.can_save_private_templates ?? false,
            can_publish_to_discover: planLimits.can_publish_to_discover ?? false,
            can_copy_discover_templates: planLimits.can_copy_discover_templates ?? false,
            can_share_papers_publicly: planLimits.can_share_papers_publicly ?? false,
            can_export_references: planLimits.can_export_references ?? false,
            favorites_enabled: planLimits.favorites_enabled ?? false,
            discover_access: planLimits.discover_access ?? 'disabled',
            upgrade_message: planLimits.upgrade_message ?? null,
          });
        }
      }
    } catch (error) {
      console.error('Error checking Stripe subscription:', error);
    }
  }, [session?.access_token, authLoading]);

  // Initial load from database
  useEffect(() => {
    fetchLocalSubscriptionData();
  }, [user]);

  // Check Stripe subscription on login only (webhook handles ongoing updates)
  useEffect(() => {
    if (authLoading || !session?.access_token) return;

    // Check once on login to sync with Stripe
    checkStripeSubscription();
  }, [session?.access_token, checkStripeSubscription]);

  // Check subscription after returning from Stripe checkout
  useEffect(() => {
    // Wait for auth to be ready before checking subscription
    if (authLoading || !session?.access_token) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('subscription') === 'success') {
      // Multiple retries to catch Stripe webhook processing
      const retryDelays = [1000, 3000, 6000, 10000];
      retryDelays.forEach((delay) => {
        setTimeout(() => {
          checkStripeSubscription();
          fetchLocalSubscriptionData();
        }, delay);
      });
      
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [checkStripeSubscription, authLoading, session?.access_token]);

  // All limits now use lifetime counts from user_usage table
  // All plans (including pro/university) check against their plan_limits
  // Fail-closed: while subscription data is loading we treat the user as at
  // limit so the UI does not allow bypassing checks before limits are known.
  const isAtLimit = (limitType: LimitType): boolean => {
    if (!limits || !usage) return true;

    switch (limitType) {
      case 'papers':
        return limits.max_papers !== -1 && usage.papers_count >= limits.max_papers;
      case 'references':
        return limits.max_references !== -1 && usage.references_count >= limits.max_references;
      case 'ai_quick_fill': {
        const resetAt = new Date(usage.ai_quick_fill_reset_at);
        // If the reset period has passed, the count is effectively 0 (will be reset on next increment)
        const effectiveCount = resetAt <= new Date() ? 0 : usage.ai_quick_fill_count;
        return limits.max_ai_quick_fill_per_month !== -1 && 
               effectiveCount >= limits.max_ai_quick_fill_per_month;
      }
      case 'collections':
        return limits.max_collections !== -1 && usage.collections_count >= limits.max_collections;
      case 'styles':
        return limits.max_reference_styles_per_collection !== -1 && usage.styles_count >= limits.max_reference_styles_per_collection;
      case 'sources':
        return limits.max_reference_sources_per_collection !== -1 && usage.sources_count >= limits.max_reference_sources_per_collection;
      default:
        return false;
    }
  };

  const hasAnyLimitReached = isAtLimit('papers') || isAtLimit('references') || isAtLimit('ai_quick_fill');
  const isLapsedPro = plan === 'lapsed_pro' || (plan === 'free' && wasPro);

  const incrementUsage = async (usageType: 'ai_quick_fill') => {
    if (!user) return;

    const { error: rpcError } = await supabase.rpc('increment_usage' as any, {
      _usage_type: usageType,
    });

    // No client-side fallback: writing user_usage directly is blocked by RLS
    // (counter mutations must go through the SECURITY DEFINER increment_usage RPC).
    if (rpcError) {
      console.error('increment_usage RPC failed:', rpcError);
      throw rpcError;
    }

    const { data: refreshedUsage, error: refreshError } = await supabase
      .from('user_usage')
      .select('papers_count, references_count, ai_quick_fill_count, ai_quick_fill_reset_at, collections_count, styles_count, sources_count')
      .eq('user_id', user.id)
      .maybeSingle();

    if (refreshError) throw refreshError;

    if (refreshedUsage) {
      setUsage({
        papers_count: refreshedUsage.papers_count,
        references_count: refreshedUsage.references_count,
        ai_quick_fill_count: refreshedUsage.ai_quick_fill_count,
        ai_quick_fill_reset_at: refreshedUsage.ai_quick_fill_reset_at,
        collections_count: refreshedUsage.collections_count,
        styles_count: refreshedUsage.styles_count,
        sources_count: refreshedUsage.sources_count,
      });
    }
  };

  // decrementUsage removed - lifetime counting means we never decrement usage counters

  const refreshUsage = async () => {
    await fetchLocalSubscriptionData();
  };

  return (
    <SubscriptionContext.Provider
      value={{
        plan,
        limits,
        usage,
        loading,
        isAtLimit,
        hasAnyLimitReached,
        incrementUsage,
        refreshUsage,
        checkStripeSubscription,
        isPro: plan === 'pro',
        isUniversity: plan === 'university',
        subscriptionEnd,
        cancelAtPeriodEnd,
        wasPro,
        isLapsedPro,
        hasPaidSubscription,
        billingPriceId,
        scheduledPriceId,
        paymentFailed,
        isTestSubscription,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}
